(function () {
  const enableAutoplay = typeof window.enableAutoplay !== "undefined" ? window.enableAutoplay : !1;
  class ProductGallery {
    constructor(container) {
      this.container = container;
      const navScope = container.closest(".pdp-gallery-block") || container;
      this.navScope = navScope;
      this.mainImages = Array.from(container.querySelectorAll(".shop-main-image"));
      this.thumbnails = Array.from(navScope.querySelectorAll(".shop-thumbnail"));
      this.dots = Array.from(navScope.querySelectorAll(".shop-dot"));
      this.desktopPrevArrow = container.querySelector(".shop-desktop-prev-arrow");
      this.desktopNextArrow = container.querySelector(".shop-desktop-next-arrow");
      this.mobilePrevArrows = Array.from(container.querySelectorAll(".shop-mobile-prev-arrow"));
      this.mobileNextArrows = Array.from(container.querySelectorAll(".shop-mobile-next-arrow"));
      this.indicatorsContainer = container.querySelector(".shop-indicators, #thumbnail-indicators");
      this.currentIndex = 0;
      this.maxIndex = this.mainImages.length - 1;
      this.visibleIndices = this.computeVisibleIndices();
      this.init();
    }
    hydrateSlide(slide) {
      if (!slide || slide.tagName !== "IMG" || slide.getAttribute("src") || !slide.dataset.src) return;
      if (slide.dataset.srcset) slide.srcset = slide.dataset.srcset;
      slide.src = slide.dataset.src;
    }
    hydrateAllSlides() {
      this.mainImages.forEach((slide) => this.hydrateSlide(slide));
    }
    setActiveSlide(index) {
      this.hydrateSlide(this.mainImages[index]);
      this.mainImages.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle("active", isActive);
        const wrapper = slide.closest(".shop-image-zoom-wrapper");
        if (wrapper) wrapper.classList.toggle("active", isActive);
      });
    }
    init() {
      const startIndex = this.getFirstVisibleIndex();
      this.setActiveSlide(startIndex);
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle("active", i === startIndex);
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle("active", i === startIndex);
      });
      this.currentIndex = startIndex;
      this.createIndicators();
      this.attachEvents();
      this.setupThumbnailStripDrag();
      this.syncThumbColumnHeight();
      this.handleVideoAutoplay(startIndex);
      const hydrateLater = () => (window.requestIdleCallback || window.setTimeout)(() => this.hydrateAllSlides());
      if (document.readyState === "complete") hydrateLater();
      else window.addEventListener("load", hydrateLater, { once: !0 });
      ["pointerenter", "touchstart", "focusin"].forEach((type) =>
        this.navScope.addEventListener(type, () => this.hydrateAllSlides(), { once: !0, passive: !0 }),
      );
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.isSwiping = !1;
      this.isPinching = !1;
      this.swipeDetectionTimer = null;
      this.swipeThreshold = 30;
      this.swipeDetectionDelay = 50; // 50ms delay to detect if second finger arrives
      const imageContainer = this.container.querySelector(".shop-main-image-wrapper");
      if (imageContainer) {
        imageContainer.addEventListener(
          "touchstart",
          (e) => {
            // Clear any pending swipe detection
            if (this.swipeDetectionTimer) {
              clearTimeout(this.swipeDetectionTimer);
              this.swipeDetectionTimer = null;
            }

            // Immediate pinch detection (2+ fingers)
            if (e.touches.length >= 2) {
              this.isPinching = !0;
              this.isSwiping = !1;
              return;
            }

            // Single finger - wait a bit to see if second finger arrives
            this.touchStartX = e.changedTouches[0].screenX;
            this.touchStartY = e.changedTouches[0].screenY;
            this.isPinching = !1;

            // Delay swipe activation to detect potential pinch
            this.swipeDetectionTimer = setTimeout(() => {
              // If timer wasn't cancelled, it's a single-finger swipe
              this.isSwiping = !0;
              this.swipeDetectionTimer = null;
            }, this.swipeDetectionDelay);
          },
          { passive: !0 },
        );
        imageContainer.addEventListener(
          "touchmove",
          (e) => {
            // Ignore swipe if user is pinching
            if (this.isPinching || !this.isSwiping) return;
            const currentTouch = e.changedTouches[0];
            const currentX = currentTouch.screenX;
            const currentY = currentTouch.screenY;
            const deltaX = currentX - this.touchStartX;
            const deltaY = currentY - this.touchStartY;
            const horizontalDominant = Math.abs(deltaX) > Math.abs(deltaY);
            if (horizontalDominant && Math.abs(deltaX) > this.swipeThreshold / 2) {
              e.preventDefault();
            }
          },
          { passive: !1 },
        );
        imageContainer.addEventListener(
          "touchend",
          (e) => {
            // Clear any pending swipe detection timer
            if (this.swipeDetectionTimer) {
              clearTimeout(this.swipeDetectionTimer);
              this.swipeDetectionTimer = null;
            }

            // Don't trigger swipe if it was a pinch gesture
            if (!this.isPinching && this.isSwiping) {
              this.touchEndX = e.changedTouches[0].screenX;
              this.handleSwipeGesture();
            }
            this.isSwiping = !1;
            this.isPinching = !1;
          },
          { passive: !0 },
        );
      }
    }
    attachEvents() {
      this.thumbnails.forEach((thumb, i) => {
        thumb.addEventListener("click", (e) => {
          if (this._thumbStripDragEnded) {
            this._thumbStripDragEnded = false;
            e.preventDefault();
            return;
          }
          this.changeImage(i);
        });
      });
      this.dots.forEach((dot, i) => {
        dot.addEventListener("click", () => {
          const rawAttr = dot.getAttribute("data-index");
          const rawIndex = rawAttr !== null ? parseInt(rawAttr, 10) : i;
          this.changeImage(rawIndex);
        });
        dot.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const rawAttr = dot.getAttribute("data-index");
            const rawIndex = rawAttr !== null ? parseInt(rawAttr, 10) : i;
            this.changeImage(rawIndex);
          }
        });
      });
      if (this.desktopPrevArrow) {
        this.desktopPrevArrow.addEventListener("click", () => {
          const newIndex = this.getPrevVisibleIndex(this.currentIndex);
          this.changeImage(newIndex);
        });
        this.desktopPrevArrow.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const newIndex = this.getPrevVisibleIndex(this.currentIndex);
            this.changeImage(newIndex);
          }
        });
      }
      if (this.desktopNextArrow) {
        this.desktopNextArrow.addEventListener("click", () => {
          const newIndex = this.getNextVisibleIndex(this.currentIndex);
          this.changeImage(newIndex);
        });
        this.desktopNextArrow.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const newIndex = this.getNextVisibleIndex(this.currentIndex);
            this.changeImage(newIndex);
          }
        });
      }
      this.mobilePrevArrows.forEach((arrow) => {
        if (arrow) {
          arrow.addEventListener("click", () => {
            const newIndex = this.getPrevVisibleIndex(this.currentIndex);
            this.changeImage(newIndex);
          });
          arrow.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              const newIndex = this.getPrevVisibleIndex(this.currentIndex);
              this.changeImage(newIndex);
            }
          });
        }
      });
      this.mobileNextArrows.forEach((arrow) => {
        if (arrow) {
          arrow.addEventListener("click", () => {
            const newIndex = this.getNextVisibleIndex(this.currentIndex);
            this.changeImage(newIndex);
          });
          arrow.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              const newIndex = this.getNextVisibleIndex(this.currentIndex);
              this.changeImage(newIndex);
            }
          });
        }
      });
      if (this.indicatorsContainer) {
        this.indicatorsContainer.addEventListener("click", (e) => {
          if (e.target.classList.contains("shop-indicator-dot")) {
            const idx = parseInt(e.target.getAttribute("data-index"));
            if (!isNaN(idx)) this.changeImage(idx);
          }
        });
      }
    }
    setupThumbnailStripDrag() {
      if (!window.matchMedia("(min-width: 800px)").matches) return;
      const self = this;
      self._activeThumbDrag = null;

      const duringDrag = (e) => {
        const a = self._activeThumbDrag;
        if (!a || a.pointerId !== e.pointerId) return;
        e.preventDefault();
        if (a.isVertical) {
          const dy = a.startY - e.clientY;
          if (!a.dragStarted && Math.abs(dy) > 5) {
            a.dragStarted = true;
          }
          if (a.dragStarted) {
            a.scrollEl.setAttribute("data-dragging", "1");
            const maxScroll = a.scrollEl.scrollHeight - a.scrollEl.clientHeight;
            const newTop = Math.max(0, Math.min(maxScroll, a.startScroll + dy));
            a.scrollEl.scrollTop = newTop;
            if (!a._loggedScroll) {
              a._loggedScroll = true;
            }
          }
        } else {
          const dx = a.startX - e.clientX;
          if (!a.dragStarted && Math.abs(dx) > 5) {
            a.dragStarted = true;
          }
          if (a.dragStarted) {
            a.scrollEl.setAttribute("data-dragging", "1");
            a.scrollEl.scrollLeft = a.startScroll + dx;
          }
        }
      };

      const stopDragging = (e) => {
        const a = self._activeThumbDrag;
        if (!a || (e && a.pointerId !== e.pointerId)) return;
        if (a.dragStarted) {
          self._thumbStripDragEnded = true;
        } else if (a.downTarget) {
          const i = self.thumbnails.indexOf(a.downTarget);
          if (i >= 0) self.changeImage(i);
        }
        a.scrollEl.removeAttribute("data-dragging");
        self._activeThumbDrag = null;
      };

      document.addEventListener("pointermove", duringDrag, { passive: false });
      document.addEventListener("pointerup", stopDragging, { passive: true });
      document.addEventListener("pointercancel", stopDragging, { passive: true });

      const strips = Array.from((this.navScope || this.container).querySelectorAll(".shop-thumbnails"));
      strips.forEach((thumbStrip) => {
        const wrapper = thumbStrip.closest(".shop-thumbnails-wrapper");
        const isVertical = wrapper && wrapper.classList.contains("left-column-layout");
        const scrollEl = thumbStrip;

        const startDragging = (e) => {
          if (e.button !== 0 && e.type === "mousedown") return;
          const startScroll = isVertical ? scrollEl.scrollTop : scrollEl.scrollLeft;
          self._activeThumbDrag = {
            scrollEl,
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            startScroll,
            isVertical,
            downTarget: e.target.closest(".shop-thumbnail"),
            dragStarted: false
          };
          scrollEl.setAttribute("data-dragging", "0");
          e.preventDefault();
        };

        if (!scrollEl) return;
        const preventNativeDrag = (e) => e.preventDefault();
        scrollEl.addEventListener("dragstart", preventNativeDrag, { capture: true });
        scrollEl.addEventListener("pointerdown", startDragging, { passive: false });
      });
    }
    syncThumbColumnHeight() {
      if (!window.matchMedia("(min-width: 768px)").matches) return;
      if (!(this.navScope || this.container).querySelector(".shop-thumbnails-wrapper.left-column-layout")) return;
      const imageContainer = this.container.querySelector(".shop-image-container");
      const root = this.container.closest(".shop-carousel-container") || this.container;
      if (!imageContainer || !root) return;
      const setHeight = () => {
        const cw = imageContainer.offsetWidth;
        const ch = imageContainer.offsetHeight;
        const activeImg = imageContainer.querySelector(".shop-image-zoom-wrapper.active .shop-main-image, .shop-main-image.active");
        const img = activeImg && activeImg.tagName === "IMG" ? activeImg : imageContainer.querySelector("img.shop-main-image.active");
        let h = ch;
        if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
          const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
          h = Math.round(img.naturalHeight * scale);
        }
        if (h > 0) root.style.setProperty("--thumb-column-height", h + "px");
      };
      setHeight();
      this._setThumbColumnHeight = setHeight;
      const ro = new ResizeObserver(() => setHeight());
      ro.observe(imageContainer);
      imageContainer.querySelectorAll("img.shop-main-image").forEach((img) => {
        if (img.complete) setHeight();
        else img.addEventListener("load", setHeight);
      });
    }
    createIndicators() {
      if (!this.indicatorsContainer) return;
      this.indicatorsContainer.innerHTML = "";
      const indices = (this.visibleIndices && this.visibleIndices.length ? this.visibleIndices : this.mainImages.map((_, i) => i));
      indices.forEach((rawIndex) => {
        const dot = document.createElement("div");
        dot.className = "shop-indicator-dot" + (rawIndex === this.currentIndex ? " active" : "");
        dot.setAttribute("data-index", rawIndex);
        this.indicatorsContainer.appendChild(dot);
      });
    }
    changeImage(index) {
      if (!this.isIndexVisible(index)) return;
      this.setActiveSlide(index);
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle("active", i === index);
      });
      this.dots.forEach((dot) => {
        const rawAttr = dot.getAttribute("data-index");
        const rawIndex = rawAttr !== null ? parseInt(rawAttr, 10) : null;
        dot.classList.toggle("active", rawIndex === index);
      });
      if (this.indicatorsContainer) {
        Array.from(this.indicatorsContainer.children).forEach((dot) => {
          const rawAttr = dot.getAttribute("data-index");
          const rawIndex = rawAttr !== null ? parseInt(rawAttr, 10) : null;
          dot.classList.toggle("active", rawIndex === index);
        });
      }
      this.currentIndex = index;
      this.scrollToThumbnail(index);
      this.handleVideoAutoplay(index);
      if (this._setThumbColumnHeight) requestAnimationFrame(() => this._setThumbColumnHeight());
    }
    scrollToThumbnail(index) {
      if (!this.thumbnails[index]) return;
      const thumbnail = this.thumbnails[index];
      const container = thumbnail.parentElement;
      if (!container) return;
      const containerWidth = container.offsetWidth;
      const thumbnailWidth = thumbnail.offsetWidth;
      const thumbnailLeft = thumbnail.offsetLeft;
      const scrollPosition = thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;
      container.scrollTo({ left: scrollPosition, behavior: "smooth" });
    }
    handleSwipeGesture() {
      const deltaX = this.touchEndX - this.touchStartX;
      if (Math.abs(deltaX) > this.swipeThreshold) {
        const currentImageIndex = this.currentIndex;
        const nextIndex = this.getNextVisibleIndex(currentImageIndex);
        const prevIndex = this.getPrevVisibleIndex(currentImageIndex);
        if (deltaX < 0) {
          this.changeImage(nextIndex);
        } else {
          this.changeImage(prevIndex);
        }
      }
      this.touchStartX = 0;
      this.touchEndX = 0;
    }
    handleVideoAutoplay(index) {
      // Pause all videos first
      this.mainImages.forEach((img, i) => {
        const video = img.querySelector("video");
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
        // Pause external videos (YouTube/Vimeo)
        const iframe = img.querySelector("iframe");
        if (iframe) {
          const src = iframe.src;
          if (src.includes("youtube.com")) {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
          } else if (src.includes("vimeo.com")) {
            iframe.contentWindow.postMessage('{"method":"pause"}', "*");
          }
        }
      });
      
      // Play the active video if autoplay is enabled
      const activeMedia = this.mainImages[index];
      if (!activeMedia) return;
      
      if (enableAutoplay) {
        // Handle local videos
        const videoToPlay = activeMedia.querySelector("video");
        if (videoToPlay) {
          videoToPlay.play().catch(() => {});
        }
        
        // Handle external videos (YouTube/Vimeo)
        const iframeToPlay = activeMedia.querySelector("iframe");
        if (iframeToPlay) {
          const src = iframeToPlay.src;
          if (src.includes("youtube.com")) {
            iframeToPlay.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', "*");
          } else if (src.includes("vimeo.com")) {
            iframeToPlay.contentWindow.postMessage('{"method":"play"}', "*");
          }
        }
      }
    }
    updateWithVariant(variant) {
      this.visibleIndices = this.computeVisibleIndices();
      let matchIndex = -1;
      if (variant && (variant.mediaId || variant.variantId || variant.imageUrl)) {
        this.mainImages.forEach((img, i) => {
          if (
            (variant.mediaId && img.dataset.mediaId == variant.mediaId) ||
            (variant.variantId && img.dataset.variantId == variant.variantId) ||
            (variant.imageUrl &&
              (img.src.includes(variant.imageUrl) || (img.dataset.src && img.dataset.src.includes(variant.imageUrl))))
          ) {
            matchIndex = i;
          }
        });
      }
      if (matchIndex >= 0 && this.isIndexVisible(matchIndex)) {
        this.changeImage(matchIndex);
      } else {
        this.changeImage(this.getFirstVisibleIndex());
      }
    }
    computeVisibleIndices() {
      const indices = [];
      this.mainImages.forEach((img, idx) => {
        const thumb = this.thumbnails[idx];
        const thumbHidden = thumb && (thumb.classList.contains("visually-hidden") || thumb.dataset.variantHidden === "true");
        const imageHidden = img.dataset.variantHidden === "true";
        if (!thumbHidden && !imageHidden) {
          indices.push(idx);
        }
      });
      return indices;
    }
    getFirstVisibleIndex() {
      if (this.visibleIndices && this.visibleIndices.length) {
        return this.visibleIndices[0];
      }
      return 0;
    }
    isIndexVisible(index) {
      if (index < 0 || index > this.maxIndex) return !1;
      if (!this.visibleIndices || !this.visibleIndices.length) return !0;
      return this.visibleIndices.indexOf(index) !== -1;
    }
    getNextVisibleIndex(currentRawIndex) {
      if (!this.visibleIndices || !this.visibleIndices.length) {
        const numImages = this.mainImages.length;
        return (currentRawIndex + 1 + numImages) % numImages;
      }
      const vis = this.visibleIndices;
      const currentPos = vis.indexOf(currentRawIndex);
      if (currentPos === -1) {
        return vis[0];
      }
      const nextPos = (currentPos + 1) % vis.length;
      return vis[nextPos];
    }
    getPrevVisibleIndex(currentRawIndex) {
      if (!this.visibleIndices || !this.visibleIndices.length) {
        const numImages = this.mainImages.length;
        return (currentRawIndex - 1 + numImages) % numImages;
      }
      const vis = this.visibleIndices;
      const currentPos = vis.indexOf(currentRawIndex);
      if (currentPos === -1) {
        return vis[vis.length - 1];
      }
      const prevPos = (currentPos - 1 + vis.length) % vis.length;
      return vis[prevPos];
    }
  }
  const galleryRegistry = [];
  function initAll() {
    document.querySelectorAll(".product-gallery").forEach((container) => {
      if (!container.__galleryInstance) {
        const gallery = new ProductGallery(container);
        container.__galleryInstance = gallery;
        galleryRegistry.push(gallery);
      }
    });
  }
  document.addEventListener("variantImageSelected", function (e) {
    if (e.detail && (e.detail.variantId || e.detail.mediaId || e.detail.imageUrl)) {
      galleryRegistry.forEach((gallery) => gallery.updateWithVariant(e.detail));
    }
  });
  document.addEventListener("variant:imageChanged", function (e) {
    if (e.detail && (e.detail.variantId || e.detail.mediaId || e.detail.imageUrl)) {
      galleryRegistry.forEach((gallery) => gallery.updateWithVariant(e.detail));
    }
  });
  document.addEventListener("shopify:section:load", function () {
    for (let i = galleryRegistry.length - 1; i >= 0; i--) {
      if (!document.contains(galleryRegistry[i].container)) {
        galleryRegistry.splice(i, 1);
      }
    }
    initAll();
  });
  window.ProductGallery = { initAll, getAll: () => galleryRegistry };
  document.addEventListener("DOMContentLoaded", initAll);
})();
